import apiClient from "../../api/api";

/**
 * Fetches detailed information for a specific user by ID.
 *
 * @async
 * @function getUserInformations
 * @param {number|string} userId - Unique identifier of the user to be retrieved.
 * @returns {Promise<Object>} Axios response containing the user information.
 *
 * @example
 * const user = await getUserInformations(1097);
 */
export const getUserInformations = async(userId) => {
    const userInformationResponse = await apiClient.get(import.meta.env.VITE_USER_BASE_URL+`/${userId}`)
    
    return userInformationResponse.data
}

/**
 * Updates an existing user's information.
 *
 * @async
 * @function updateUserRequest
 * @param {number|string} userId - Unique identifier of the user to be updated.
 * @param {Object} userInformations - Object containing the fields to be updated.
 * @returns {Promise<Object>} Updated user data returned by the API.
 *
 * @example
 * await updateUserRequest(1097, { firstName: "Vinicius", lastName: "Vendrame" });
 */
export const updateUserRequest = async(userId, userInformations) => {
    const updateUserResponse = await apiClient.patch(import.meta.env.VITE_USER_BASE_URL+`/${userId}`, userInformations)
    return updateUserResponse.data
}


/**
 * Updates the profile photo of a specific user.
 *
 * @async
 * @function updateUserPhotoRequest
 * @param {number|string} userId - Unique identifier of the user.
 * @param {Object|string} key - Payload or key reference of the new profile photo (e.g. S3 key or image identifier).
 * @returns {Promise<Object>} Updated user data returned by the API.
 *
 * @example
 * await updateUserPhotoRequest(1097, { photoKey: "users/1097/profile.jpg" });
 */
export const updateUserPhotoRequest = async(userId, key) => {
    const updateResponse = await apiClient.patch(import.meta.env.VITE_USER_BASE_URL+`/${userId}/photo`, key, {successToast:"User photo updated"})
    
    return updateResponse.data
}

/**
 * @description Fetches all active users from the API.
 * The request is paginated with a fixed page and limit, and disabled users are excluded.
 *
 * @async
 * @function requestGetAllUser
 * @returns {Promise<any>} A promise that resolves with the API response data containing the users list.
 *
 * @throws {Error} Throws an error if the API request fails.
 */
export const requestGetAllUser = async(search, page = 1, limit = 1000) => {
    const params = {
        search,
        page: page,
        limit: limit,
        canShowDisabled: false
    }
    const getResponse = await apiClient.get(import.meta.env.VITE_USER_BASE_URL, {params})

    return getResponse.data
}

export const updateUserSystemRole = async(userId, role) => {
    await apiClient.patch(import.meta.env.VITE_USER_BASE_URL+`/${userId}/role`, role, {successToast:"User role updated"})
}

export const getUserAchievements = async(userId) => {
    const response = await apiClient.get(import.meta.env.VITE_USER_BASE_URL+`/${userId}/achievements`)
    return response.data

}   

export const getBirthdays = async(month, disabled = false) => {
    const params = {
        page: 1,
        limit: 100,
        month: month,
        disabled: disabled
    }
    const response = await apiClient.get(import.meta.env.VITE_USER_BASE_URL+`/birthdays`, {params})
    return response.data
}

export const getAchievements = async(userId) => {
    const response = await apiClient.get(import.meta.env.VITE_USER_BASE_URL+`/${userId}/achievement-list`)
    
    return response.data
}

export const getUsersStatus = async (projectId = 0, limit = 1000) => {
  const users = [];
  let page = 1;
  let totalPages = 1;

  do {
    const params = {
      page,
      limit,
    };

    if (projectId > 0) {
      params.projectIds = projectId;
    }

    const response = await apiClient.get(
      `${import.meta.env.VITE_USER_BASE_URL}/status`,
      { params }
    );

    const responseData = response.data;
    const pageUsers = responseData?.data || [];
    const pagination = responseData?.pagination;

    users.push(...pageUsers);

    if (pagination?.totalResults) {
      const pageSize = pagination.pageSize || limit;

      totalPages = Math.ceil(
        pagination.totalResults / pageSize
      );
    } else {
      totalPages = pageUsers.length === limit
        ? page + 1
        : page;
    }

    page++;
  } while (page <= totalPages);

  return users;
};